import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from "react";
import { User, Session } from "@supabase/supabase-js";
import { syncSentryUserFromSupabase } from "@/integrations/sentry/sync-user";
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";

type AdminRole = Database["public"]["Enums"]["admin_role"];

// ─── localStorage keys ────────────────────────────────────────────────────────
const ADMIN_BACKUP_KEY = "syntra_admin_backup";
const IMPERSONATED_AS_KEY = "syntra_impersonated_as";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  /** True once `admin_roles` has been loaded for the current user (or there is no user). */
  adminRoleReady: boolean;
  emailVerified: boolean;
  /** Row in `public.admin_roles` for this user, if any. */
  syntraAdminRole: AdminRole | null;
  /** Admin console: any of `super_admin`, `support`, or `ops` in `admin_roles`. */
  canAccessAdmin: boolean;
  /** Email of the user being impersonated, or null. */
  impersonatedAs: string | null;
  /** True after Supabase emits PASSWORD_RECOVERY (user opened reset link). */
  passwordRecoveryActive: boolean;
  /** Call after successful password update or to abandon recovery UI. */
  clearPasswordRecovery: () => void;
  /**
   * True while the signup handler is still provisioning the account. `signUp`
   * emits SIGNED_IN before the profile row (and the workspace its trigger
   * creates) exists, so anything that reacts to "there is a session" must wait
   * for this to clear — see `PublicRoute`.
   */
  signupInProgress: boolean;
  setSignupInProgress: (value: boolean) => void;
  signOut: () => Promise<void>;
  resendVerification: (email: string) => Promise<void>;
  /**
   * Switch the active Supabase session to a target user.
   * Saves the current admin session to localStorage so it can be restored.
   * @param targetEmail  Email shown in the UI banner.
   * @param hashedToken  Token returned by the `impersonate-user` Edge Function.
   */
  startImpersonation: (
    targetEmail: string,
    hashedToken: string,
  ) => Promise<void>;
  /** Restore the original admin session that was saved before impersonation. */
  endImpersonation: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  isLoading: true,
  adminRoleReady: true,
  emailVerified: false,
  syntraAdminRole: null,
  canAccessAdmin: false,
  impersonatedAs: null,
  passwordRecoveryActive: false,
  clearPasswordRecovery: () => {},
  signupInProgress: false,
  setSignupInProgress: () => {},
  signOut: async () => {},
  resendVerification: async () => {},
  startImpersonation: async () => {},
  endImpersonation: async () => {},
});

function canAccessAdminFromRole(role: AdminRole | null): boolean {
  return role === "super_admin" || role === "support" || role === "ops";
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [syntraAdminRole, setSyntraAdminRole] = useState<AdminRole | null>(
    null,
  );
  const [adminRoleReady, setAdminRoleReady] = useState(true);
  const [impersonatedAs, setImpersonatedAs] = useState<string | null>(() =>
    localStorage.getItem(IMPERSONATED_AS_KEY),
  );
  const [passwordRecoveryActive, setPasswordRecoveryActive] = useState(false);
  const [signupInProgress, setSignupInProgress] = useState(false);

  const clearPasswordRecovery = () => {
    setPasswordRecoveryActive(false);
  };

  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "PASSWORD_RECOVERY") {
        setPasswordRecoveryActive(true);
      }
      setSession(session);
      setUser(session?.user ?? null);
      setIsLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Reload admin role whenever the active user changes
  useEffect(() => {
    const uid = user?.id;
    if (!uid) {
      setSyntraAdminRole(null);
      setAdminRoleReady(true);
      return;
    }

    let cancelled = false;
    setAdminRoleReady(false);

    (async () => {
      const { data, error } = await supabase
        .from("admin_roles")
        .select("role")
        .eq("user_id", uid)
        .maybeSingle();

      if (cancelled) return;

      if (error) {
        setSyntraAdminRole(null);
      } else {
        setSyntraAdminRole(data?.role ?? null);
      }
      setAdminRoleReady(true);
    })();

    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  useEffect(() => {
    syncSentryUserFromSupabase(user);
  }, [user]);

  const signOut = async () => {
    localStorage.removeItem(ADMIN_BACKUP_KEY);
    localStorage.removeItem(IMPERSONATED_AS_KEY);
    setImpersonatedAs(null);
    setPasswordRecoveryActive(false);
    await supabase.auth.signOut();
  };

  const resendVerification = async (email: string) => {
    const { error } = await supabase.auth.resend({ type: "signup", email });
    if (error) throw error;
  };

  const startImpersonation = async (
    targetEmail: string,
    hashedToken: string,
  ) => {
    // Persist the admin's current session so we can restore it later
    if (session) {
      localStorage.setItem(
        ADMIN_BACKUP_KEY,
        JSON.stringify({
          access_token: session.access_token,
          refresh_token: session.refresh_token,
        }),
      );
    }
    localStorage.setItem(IMPERSONATED_AS_KEY, targetEmail);
    setImpersonatedAs(targetEmail);

    // Switch Supabase client to the target user's session
    const { error } = await supabase.auth.verifyOtp({
      token_hash: hashedToken,
      type: "magiclink",
    });

    if (error) {
      // Roll back on failure
      localStorage.removeItem(ADMIN_BACKUP_KEY);
      localStorage.removeItem(IMPERSONATED_AS_KEY);
      setImpersonatedAs(null);
      throw error;
    }
  };

  const endImpersonation = async () => {
    const backupJson = localStorage.getItem(ADMIN_BACKUP_KEY);
    if (backupJson) {
      const { access_token, refresh_token } = JSON.parse(backupJson) as {
        access_token: string;
        refresh_token: string;
      };
      // Restoring the session triggers onAuthStateChange → admin user/role reloaded
      await supabase.auth.setSession({ access_token, refresh_token });
    }
    localStorage.removeItem(ADMIN_BACKUP_KEY);
    localStorage.removeItem(IMPERSONATED_AS_KEY);
    setImpersonatedAs(null);
  };

  const emailVerified = user?.email_confirmed_at != null;
  const canAccessAdmin = canAccessAdminFromRole(syntraAdminRole);

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        isLoading,
        adminRoleReady,
        emailVerified,
        syntraAdminRole,
        canAccessAdmin,
        impersonatedAs,
        passwordRecoveryActive,
        clearPasswordRecovery,
        signupInProgress,
        setSignupInProgress,
        signOut,
        resendVerification,
        startImpersonation,
        endImpersonation,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
